import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Star, Mail } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import ConsentCheckbox from "./ConsentCheckbox";

export default function LeadMagnetSection() {
    const { pathname } = useLocation();
    const [config, setConfig] = useState(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get("/lead-magnet/config")
            .then((r) => { if (!cancelled) setConfig(r.data); })
            .catch(() => { if (!cancelled) setConfig({ enabled: false }); });
        return () => { cancelled = true; };
    }, []);

    if (!config) return null;
    if (!config.enabled) return null;
    if (!config.has_pdf) return null;
    if (!Array.isArray(config.display_routes) || !config.display_routes.includes(pathname)) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) {
            toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
            return;
        }
        setLoading(true);
        try {
            const res = await api.post("/leads", {
                name: name.trim(),
                email: email.trim(),
                source: `lead_magnet:${pathname}`,
            });
            setDone(true);
            if (res.data?.email_status === "pending_confirmation") {
                toast.success("Bestätigungs-Mail versendet — bitte Posteingang prüfen.");
            } else if (res.data?.email_status === "already_confirmed") {
                toast.success("Sie sind bereits bestätigt — wir senden Ihnen das PDF erneut.");
            } else {
                toast.success("Vielen Dank! Wir melden uns gleich.");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="py-24 lg:py-32 bg-aimploy-bg" data-testid="lead-magnet-section">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <div className="bg-aimploy-navy text-white rounded-3xl p-10 lg:p-20 relative overflow-hidden grain">
                    <div className="absolute inset-0 bg-dots-dark opacity-40" />
                    <div className="absolute -bottom-32 -right-20 h-[400px] w-[400px] rounded-full bg-aimploy-violet/30 blur-3xl" />
                    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <span className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">
                                {config.eyebrow}
                            </span>
                            <h2 className="font-display text-4xl lg:text-5xl font-bold leading-[1] mt-4 whitespace-pre-line">
                                {config.title}
                            </h2>
                            <p className="text-white/70 mt-6 max-w-md leading-relaxed">{config.description}</p>
                            {Array.isArray(config.bullets) && config.bullets.length > 0 && (
                                <ul className="mt-8 space-y-3 text-sm text-white/80">
                                    {config.bullets.map((b, i) => (
                                        <li key={i} className="flex gap-3">
                                            <span className="h-5 w-5 rounded-full bg-aimploy-violet flex items-center justify-center mt-0.5 flex-shrink-0">
                                                <Star className="w-3 h-3 fill-white text-white" />
                                            </span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {done ? (
                            <div className="bg-white text-aimploy-navy rounded-2xl p-10 text-center">
                                <div className="h-14 w-14 rounded-full bg-aimploy-violet text-white flex items-center justify-center mx-auto mb-4">
                                    <Mail className="w-6 h-6" />
                                </div>
                                <h3 className="font-display text-2xl font-bold mb-2" data-testid="lead-magnet-success-title">
                                    {config.success_title}
                                </h3>
                                <p className="text-aimploy-muted">{config.success_text}</p>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="lead-magnet-form"
                                className="bg-white text-aimploy-navy rounded-2xl p-8 lg:p-10 space-y-4"
                            >
                                <h3 className="font-display text-2xl font-bold">{config.form_title}</h3>
                                <input
                                    type="text"
                                    placeholder="Ihr Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    data-testid="lead-magnet-name"
                                    className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5 text-sm"
                                />
                                <input
                                    type="email"
                                    placeholder="Geschäftliche E-Mail"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="lead-magnet-email"
                                    className="w-full border border-aimploy-navy/15 rounded-xl px-5 py-3.5 text-sm"
                                />
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="lead-magnet-submit"
                                    className="w-full bg-aimploy-violet hover:bg-aimploy-violet-hover text-white px-6 py-3.5 rounded-xl text-sm font-semibold disabled:opacity-60 transition-colors"
                                >
                                    {loading ? "Wird gesendet..." : config.form_cta}
                                </button>
                                <ConsentCheckbox checked={consent} onChange={setConsent} testid="lead-magnet-consent" />
                                <p className="text-xs text-aimploy-muted">{config.form_disclaimer}</p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
